import { memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { getLoadstoneStaggerDelayMs } from './loadstone-model'
import type { LoadstoneColorMode, LoadstoneConfig } from './loadstone-types'

const BASE_SIZE = 48

export interface LoadstoneProps {
  className?: string
  colorMode?: LoadstoneColorMode
  config: LoadstoneConfig
  label?: string
  paused?: boolean
  size?: number
}

interface LoadstoneLayout {
  cellSize: number
  gap: number
  originX: number
  originY: number
  scale: number
}

interface LoadstoneContainerStyle {
  '--loadstone-cell-size': string
  '--loadstone-duration': string
  '--loadstone-led-glow': string
  '--loadstone-play-state': 'paused' | 'running'
  '--loadstone-radius': string
  colorScheme: 'light' | 'dark' | 'light dark'
  height: number
  width: number
}

interface LoadstoneCellStyle {
  '--loadstone-cell-color': string
  '--loadstone-cell-delay': string
  '--loadstone-cell-opacity': number
  '--loadstone-cell-scale': number
  left: number
  top: number
}

function calculateLoadstoneLayout(config: LoadstoneConfig, size: number): LoadstoneLayout {
  const scale = size / BASE_SIZE
  const longestAxis = Math.max(config.rows, config.columns)
  const maxGap = longestAxis > 1 ? Math.max(Math.floor((size - longestAxis) / (longestAxis - 1)), 0) : 0
  const gap = Math.min(Math.max(Math.round(config.gap * scale), 0), maxGap)
  const cellSize = Math.max(
    Math.floor(
      Math.min((size - gap * (config.columns - 1)) / config.columns, (size - gap * (config.rows - 1)) / config.rows),
    ),
    1,
  )
  const gridWidth = cellSize * config.columns + gap * (config.columns - 1)
  const gridHeight = cellSize * config.rows + gap * (config.rows - 1)

  return {
    cellSize,
    gap,
    originX: Math.floor((size - gridWidth) / 2),
    originY: Math.floor((size - gridHeight) / 2),
    scale,
  }
}

function getAnimationPhaseKey(config: LoadstoneConfig): string {
  return [config.duration, config.stagger, ...config.cells.map((cell) => cell.order)].join('-')
}

export const Loadstone = memo(function Loadstone({
  className,
  colorMode,
  config,
  label = 'Working',
  paused = false,
  size = 32,
}: LoadstoneProps): JSX.Element {
  const { cellSize, gap, originX, originY, scale } = calculateLoadstoneLayout(config, size)

  // Remount when animation phases change so all cells restart in sync
  const animationPhaseKey = getAnimationPhaseKey(config)
  const cellClassName = `loadstone-cell loadstone-cell-${config.animation}${config.ledMode ? ' loadstone-cell-led' : ''}`
  const containerStyle: LoadstoneContainerStyle = {
    width: size,
    height: size,
    // light-dark() in cell colors resolves against this scheme — no JS theme subscription
    colorScheme: colorMode ?? 'light dark',
    '--loadstone-cell-size': `${cellSize}px`,
    '--loadstone-radius': `${Math.round(config.radius * scale)}px`,
    '--loadstone-duration': `${config.duration}ms`,
    '--loadstone-play-state': paused ? 'paused' : 'running',
    '--loadstone-led-glow': `${Math.min(Math.max(size / 10, 1.5), 6)}px`,
  }

  return (
    <span
      key={animationPhaseKey}
      role="status"
      aria-label={label}
      className={twMerge('relative inline-block shrink-0', className)}
      style={containerStyle}
    >
      {config.cells.map((cell) => {
        const lightColor = cell.lightColor ?? cell.darkColor ?? 'currentColor'
        const darkColor = cell.darkColor ?? lightColor
        const style: LoadstoneCellStyle = {
          '--loadstone-cell-color': lightColor === darkColor ? lightColor : `light-dark(${lightColor}, ${darkColor})`,
          '--loadstone-cell-opacity': cell.opacity,
          '--loadstone-cell-scale': cell.scale,
          '--loadstone-cell-delay': `${getLoadstoneStaggerDelayMs(cell.order, config.duration, config.stagger)}ms`,
          left: originX + cell.column * (cellSize + gap),
          top: originY + cell.row * (cellSize + gap),
        }

        return <span key={`${cell.row}-${cell.column}`} className={cellClassName} style={style} />
      })}
    </span>
  )
})
